import React, { useState, useEffect, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import { computePreviewValues, findExtremeRowsFromStream, getRowsByIndices } from "../utils/fileParser";

interface PreviewVariablesModalProps {
  open: boolean;
  onClose: () => void;
  data: Record<string, string>[];
  applyPreview?: (previewValues: Record<string, string>) => void;
  clearPreview?: () => void;
  editor?: any;
  designId?: string;
  campaignId?: string;
  format?: string;
  /** Optional callback to fetch data on modal open (e.g., audience CSV) */
  onFetchData?: () => Promise<Record<string, string>[] | null>;
  /**
   * Source files for streaming extreme row detection (for large files).
   * Each entry can be a File (local upload) or signed URL string (S3).
   * When provided, streams through the full CSV to find the real shortest/longest rows
   * instead of using the limited sample data.
   */
  streamingSources?: Array<{ source: File | string; headers?: string[] }>;
  /**
   * Async callback to resolve additional streaming sources (e.g., get presigned URLs for S3 files).
   * Called when the modal opens. Returns sources to add to streamingSources.
   */
  resolveStreamingSources?: () => Promise<Array<{ source: File | string; headers?: string[] }>>;
}

const PreviewVariablesModal: React.FC<PreviewVariablesModalProps> = ({
  open,
  onClose: onCloseProp,
  data: dataProp,
  applyPreview,
  clearPreview,
  editor,
  designId,
  campaignId,
  format,
  onFetchData,
  streamingSources,
  resolveStreamingSources,
}) => {
  // Wrap onClose to always restore canvas placeholders before closing
  const onClose = useCallback(() => {
    clearPreview?.();
    onCloseProp();
  }, [clearPreview, onCloseProp]);
  const [shortestImages, setShortestImages] = useState<string[]>([]);
  const [longestImages, setLongestImages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedImage, setExpandedImage] = useState<{ url: string; label: string } | null>(null);
  const isGeneratingRef = useRef(false);

  // Build a detached HTML snapshot of the canvas for one row. Serial: it mutates
  // and clones the shared canvas. Rendering the result afterwards is canvas-free.
  const buildFlyerHtml = useCallback(
    async (
      previewValues: Record<string, string>
    ): Promise<{
      htmlString: string;
      customDimensions: any;
      imageResolution?: { width: number; height: number };
    } | null> => {
      if (!editor) return null;

      try {
        await applyPreview?.(previewValues);
        // Wait for canvas to settle after applying preview values
        await new Promise((r) => setTimeout(r, 500));

        const simpleHtmlExport = (await import("pages/flyer/builder/drawer/new-tldraw-components/simpleHtmlExport"))
          .default;
        const { getFormatDimensionsWithOrientation } = await import("pages/flyer/builder/utils/formatDimensions");

        const htmlContent = await simpleHtmlExport({
          designId,
          campaignId,
          format,
          returnHtml: true,
          editor,
          keepCameraPosition: true,
          displaySafetyAndCutLines: false,
        });

        if (!htmlContent) {
          console.error("[PreviewModal] buildFlyerHtml — simpleHtmlExport returned empty");
          return null;
        }

        // Read return address from the customAddressBox shape on the canvas
        // This is the value the user configured in the flyer builder, not from the CSV
        let returnAddressValue = "";
        try {
          const currentPageId = editor.getCurrentPageId();
          const allShapeIds = editor.getSortedChildIdsForParent(currentPageId);
          for (const sid of allShapeIds) {
            const shape = editor.getShape(sid);
            if (shape?.type === "customAddressBox") {
              const props = (shape as any).props;
              returnAddressValue = props?.displayReturnAddress || props?.returnAddress || "";
              if (returnAddressValue && returnAddressValue !== "{{return_address}}") break;
            }
          }
        } catch (e) {
          console.warn("[PreviewModal] Could not read return address from canvas:", e);
        }

        // Replace {{variable}} placeholders in the exported HTML.
        // This covers addressbox fields (full_name, address, city, state, zip, etc.)
        // which are hardcoded in the customAddressBox shape and not modified by applyPreview.
        // Also handles HTML-encoded braces (&#123;&#123; / &#125;&#125;) that some renderers produce.

        // Addressbox field aliases: maps addressbox variable names to common CSV column names
        const ADDRESS_ALIASES: Record<string, string[]> = {
          full_name: ["full_name", "fullname", "name"],
          address: ["address", "address_1", "address1", "street"],
          city: ["city", "ciudad", "town"],
          state: ["state", "st", "province"],
          zip: ["zip", "zip_code", "zipcode", "postal_code", "postal code"],
          return_address: ["return_address", "returnaddress"],
          endorse: ["endorse"],
          cont_id: ["cont_id", "contid"],
          gpb_id: ["gpb_id", "gpbid"],
          sequence: ["sequence", "seq"],
          crrt: ["crrt"],
        };

        // Build a lookup: lowered alias → preview value
        const aliasLookup: Record<string, string> = {};
        for (const [canonical, aliases] of Object.entries(ADDRESS_ALIASES)) {
          for (const alias of aliases) {
            const ciKey = Object.keys(previewValues).find(
              (k) => k.toLowerCase() === alias || k.toLowerCase().replace(/\s+/g, "_") === alias
            );
            if (ciKey) {
              aliasLookup[canonical] = previewValues[ciKey];
              break;
            }
          }
        }

        // Use the return address from the canvas (not from CSV)
        if (returnAddressValue && returnAddressValue !== "{{return_address}}") {
          aliasLookup["return_address"] = returnAddressValue;
        }

        // Dummy values for USPS postal fields (generated at print time, not from CSV)
        if (!aliasLookup["endorse"]) aliasLookup["endorse"] = "OR CURRENT RESIDENT";
        if (!aliasLookup["cont_id"]) aliasLookup["cont_id"] = "C00142";
        if (!aliasLookup["gpb_id"]) aliasLookup["gpb_id"] = "GPB-0038";
        if (!aliasLookup["sequence"]) aliasLookup["sequence"] = "000001";
        if (!aliasLookup["crrt"]) aliasLookup["crrt"] = "C021";

        // Sanitize HTML to prevent injection via CSV values
        const escapeHtml = (str: string): string =>
          str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

        const replacePlaceholder = (_match: string, varName: string): string => {
          const trimmed = varName.trim();
          const lowerTrimmed = trimmed.toLowerCase();

          // Direct match
          if (previewValues[trimmed] !== undefined) return escapeHtml(previewValues[trimmed]);

          // Title Case match (e.g., postal_code → Postal Code)
          const titleCase = trimmed
            .split("_")
            .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(" ");
          if (previewValues[titleCase] !== undefined) return escapeHtml(previewValues[titleCase]);

          // Case-insensitive match
          const ciKey = Object.keys(previewValues).find(
            (k) => k.toLowerCase() === lowerTrimmed || k.toLowerCase().replace(/\s+/g, "_") === lowerTrimmed
          );
          if (ciKey !== undefined) return escapeHtml(previewValues[ciKey]);

          // Addressbox alias match
          if (aliasLookup[lowerTrimmed] !== undefined) return escapeHtml(aliasLookup[lowerTrimmed]);

          // Construct full_name from first_name + last_name
          if (lowerTrimmed === "full_name" || lowerTrimmed === "fullname") {
            const fn = aliasLookup["full_name"];
            if (fn) return escapeHtml(fn);
            const firstName =
              previewValues["first_name"] ||
              previewValues["First Name"] ||
              previewValues["first"] ||
              previewValues["Nombre"] ||
              "";
            const lastName =
              previewValues["last_name"] ||
              previewValues["Last Name"] ||
              previewValues["last"] ||
              previewValues["Surname"] ||
              "";
            if (firstName || lastName) return escapeHtml(`${firstName} ${lastName}`.trim());
          }

          return _match; // Keep placeholder if no match found
        };

        // Replace standard {{variable}} patterns
        let finalHtml = htmlContent.replace(/\{\{\s*([a-zA-Z0-9_.\- ]+)\s*\}\}/g, replacePlaceholder);

        // Also replace HTML-encoded variants: &#123;&#123;var&#125;&#125; or %7B%7Bvar%7D%7D
        finalHtml = finalHtml.replace(/&#123;&#123;\s*([a-zA-Z0-9_.\- ]+)\s*&#125;&#125;/g, replacePlaceholder);
        finalHtml = finalHtml.replace(/%7B%7B\s*([a-zA-Z0-9_.\- ]+)\s*%7D%7D/gi, replacePlaceholder);

        const customDimensions = getFormatDimensionsWithOrientation(format || "", designId, campaignId);

        const { getEffectiveFormat, isRuntimeFormat } = await import("pages/flyer/builder/formats/formatsStore");
        const { DocumentOrientation } = await import("pages/flyer/builder/drawer/types");
        const { getStoredOrientation } = await import("pages/flyer/builder/utils/orientationStorage");

        const formatConfig = getEffectiveFormat(format);
        let imageResolution;

        if (formatConfig) {
          const storedOrientation = getStoredOrientation(
            designId,
            campaignId,
            formatConfig.defaultOrientation ?? DocumentOrientation.Portrait
          );
          const isPortrait = storedOrientation === DocumentOrientation.Portrait;
          const isLandscapeEnabled = isRuntimeFormat(formatConfig)
            ? formatConfig.allowLandscape
            : format !== "TEARAWAY" && format !== "DOOR_HANGER";

          const cfgW: number = (formatConfig as any).displayWidth || (formatConfig as any).width || 419.5;
          const cfgH: number = (formatConfig as any).displayHeight || (formatConfig as any).height || 582;
          const isNaturallyLandscape = cfgW > cfgH;

          let pageWidthPx: number, pageHeightPx: number;

          if (isPortrait) {
            if (isNaturallyLandscape) {
              pageWidthPx = cfgH;
              pageHeightPx = cfgW;
            } else {
              pageWidthPx = cfgW;
              pageHeightPx = cfgH;
            }
          } else if (!isLandscapeEnabled) {
            pageWidthPx = cfgW;
            pageHeightPx = cfgH;
          } else {
            if (!isNaturallyLandscape) {
              pageWidthPx = cfgH;
              pageHeightPx = cfgW;
            } else {
              pageWidthPx = cfgW;
              pageHeightPx = cfgH;
            }
          }

          imageResolution = {
            width: Math.round(pageWidthPx * 2 + 10),
            height: Math.round(pageHeightPx),
          };
        }

        return { htmlString: finalHtml, customDimensions, imageResolution };
      } catch (err) {
        console.error("[PreviewModal] Error building preview flyer HTML:", err);
        return null;
      }
    },
    [editor, applyPreview, designId, campaignId, format]
  );

  // Render a built snapshot to PNG. No canvas access, so calls can run in parallel
  // (puppeteer allows up to 5 concurrent single-file requests).
  const renderFlyer = useCallback(
    async (
      built: {
        htmlString: string;
        customDimensions: any;
        imageResolution?: { width: number; height: number };
      } | null
    ): Promise<string | null> => {
      if (!built) return null;
      try {
        const { generateFileFromService } = await import("module/puppeteerService/generatePDF");
        const res = await generateFileFromService({
          htmlString: built.htmlString,
          fileType: "PNG",
          includeBleedsAndCropMarks: false,
          customDimensions: built.customDimensions,
          useRowLayout: true,
          ...(built.imageResolution && { imageResolution: built.imageResolution }),
        });
        return res?.s3Url || null;
      } catch (err) {
        console.error("[PreviewModal] Error rendering preview flyer:", err);
        return null;
      }
    },
    []
  );

  useEffect(() => {
    if (!open || !editor) {
      isGeneratingRef.current = false;
      return;
    }
    // Guard: only run once per modal open
    if (isGeneratingRef.current) return;
    isGeneratingRef.current = true;

    let cancelled = false;

    const generatePreviews = async () => {
      setLoading(true);
      setError(null);
      setShortestImages([]);
      setLongestImages([]);
      setExpandedImage(null);

      try {
        // Resolve data: use prop data, or fetch via onFetchData callback
        let data = dataProp;
        if ((!data || data.length === 0) && onFetchData) {
          const fetched = await onFetchData();
          if (cancelled) return;
          data = fetched || [];
        }

        if (data.length === 0) {
          setError("No data available for preview.");
          return;
        }

        // Determine shortest/longest rows — use streaming for large files, sample for small
        let shortestValues: Record<string, string>;
        let shortest2Values: Record<string, string>;
        let longestValues: Record<string, string>;
        let longest2Values: Record<string, string>;

        // Collect all streaming sources (static + dynamically resolved)
        const allSources: Array<{ source: File | string; headers?: string[] }> = [...(streamingSources || [])];
        if (resolveStreamingSources) {
          try {
            const resolved = await resolveStreamingSources();
            if (cancelled) return;
            allSources.push(...resolved);
          } catch (e) {
            // Fallback to sample data if resolution fails
          }
        }

        if (allSources.length > 0) {
          // Strategy: use the FIRST source (typically audience) to find extreme row indices.
          // Then extract matching rows from ALL other sources by the same indices.
          // This ensures promo_code/qr_link from variable file match the same person.

          const primarySource = allSources[0];
          const secondarySources = allSources.slice(1);

          if (cancelled) return;
          const primaryExtremes = await findExtremeRowsFromStream(primarySource.source, primarySource.headers);

          if (primaryExtremes) {
            // Start with primary CSV data
            let mergedShortest = { ...primaryExtremes.shortest };
            let mergedShortest2 = { ...primaryExtremes.shortest2 };
            let mergedLongest = { ...primaryExtremes.longest };
            let mergedLongest2 = { ...primaryExtremes.longest2 };

            // For each secondary source, extract the rows at the same indices
            const targetIndices = [
              primaryExtremes.shortestIdx,
              primaryExtremes.shortest2Idx,
              primaryExtremes.longestIdx,
              primaryExtremes.longest2Idx,
            ].filter((idx) => idx >= 0);

            for (const { source, headers } of secondarySources) {
              if (cancelled) return;
              const rowsByIdx = await getRowsByIndices(source, targetIndices, headers);

              // Merge secondary data into each extreme set using matching row indices
              const shortRow = rowsByIdx[primaryExtremes.shortestIdx];
              const short2Row = rowsByIdx[primaryExtremes.shortest2Idx];
              const longRow = rowsByIdx[primaryExtremes.longestIdx];
              const long2Row = rowsByIdx[primaryExtremes.longest2Idx];

              if (shortRow) mergedShortest = { ...mergedShortest, ...shortRow };
              if (short2Row) mergedShortest2 = { ...mergedShortest2, ...short2Row };
              if (longRow) mergedLongest = { ...mergedLongest, ...longRow };
              if (long2Row) mergedLongest2 = { ...mergedLongest2, ...long2Row };
            }

            shortestValues = mergedShortest;
            shortest2Values = mergedShortest2;
            longestValues = mergedLongest;
            longest2Values = mergedLongest2;
          } else {
            // Primary streaming failed — fall back to sample data
            shortestValues = computePreviewValues(data, "shortest");
            shortest2Values = computeSecondPreviewValues(data, "shortest");
            longestValues = computePreviewValues(data, "longest");
            longest2Values = computeSecondPreviewValues(data, "longest");
          }
        } else {
          // Use sample data (small files or no streaming sources) — compute from sample
          shortestValues = computePreviewValues(data, "shortest");
          longestValues = computePreviewValues(data, "longest");
          // For 2nd extremes from sample, pick 2nd-best per column
          shortest2Values = computeSecondPreviewValues(data, "shortest");
          longest2Values = computeSecondPreviewValues(data, "longest");
        }

        // Capture the 4 snapshots serially (they share the live canvas).
        const valueSets = [shortestValues, shortest2Values, longestValues, longest2Values];
        const builtHtmls: Array<Awaited<ReturnType<typeof buildFlyerHtml>>> = [];
        for (const values of valueSets) {
          if (cancelled) return;
          builtHtmls.push(await buildFlyerHtml(values));
        }
        if (cancelled) return;

        // Render them in parallel — the slow part; no canvas race.
        const [shortImg1, shortImg2, longImg1, longImg2] = await Promise.all(
          builtHtmls.map((built) => renderFlyer(built))
        );
        if (cancelled) return;

        setShortestImages([shortImg1, shortImg2].filter(Boolean) as string[]);
        setLongestImages([longImg1, longImg2].filter(Boolean) as string[]);

        clearPreview?.();
      } catch (err) {
        console.error("Error generating previews:", err);
        setError("Failed to generate previews. Please try again.");
        clearPreview?.();
      } finally {
        if (!cancelled) {
          setLoading(false);
          isGeneratingRef.current = false;
        }
      }
    };

    generatePreviews();

    return () => {
      cancelled = true;
      isGeneratingRef.current = false;
    };
    // Only re-run when modal opens/closes — NOT when data/callbacks change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editor]);

  if (!open) return null;

  const renderImageCard = (url: string, label: string, key: string) => (
    <div
      key={key}
      onClick={() => setExpandedImage({ url, label })}
      style={{
        flex: 1,
        border: "1px solid #E2E2FC",
        borderRadius: 8,
        overflow: "hidden",
        background: "#FAFAFE",
        cursor: "pointer",
        transition: "box-shadow 0.2s, transform 0.2s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)";
        e.currentTarget.style.transform = "translateY(-2px)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <img src={url} alt={label} style={{ width: "100%", height: "auto", display: "block" }} />
      <div
        style={{
          padding: "6px 10px",
          fontSize: 10,
          color: "#798498",
          textAlign: "center",
          borderTop: "1px solid #E2E2FC",
        }}
      >
        Click to enlarge
      </div>
    </div>
  );

  const modal = (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: '"Inter", sans-serif',
      }}
    >
      {/* Backdrop */}
      {/* Backdrop — no onClick, modal only closes via buttons */}
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)" }} />

      {/* Modal */}
      <div
        style={{
          position: "relative",
          background: "#FFF",
          borderRadius: 12,
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          width: 900,
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "16px 24px",
            borderBottom: "1px solid #E2E2FC",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#0C172C" }}>Preview Variables</h2>
          <svg onClick={onClose} width="20" height="20" viewBox="0 0 20 20" fill="none" style={{ cursor: "pointer" }}>
            <path
              d="M5 5L15 15M15 5L5 15"
              stroke="#2E3860"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        {/* Content */}
        <div style={{ padding: 24, overflowY: "auto", flex: 1 }}>
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: 60 }}>
              <div
                style={{
                  width: 40,
                  height: 40,
                  border: "3px solid #E2E2FC",
                  borderTopColor: "#F43F5E",
                  borderRadius: "50%",
                  animation: "spin 0.6s linear infinite",
                }}
              />
              <p style={{ fontSize: 13, color: "#798498", margin: 0 }}>Generating flyer previews...</p>
              <p style={{ fontSize: 11, color: "#A0A8B8", margin: 0 }}>This may take a moment</p>
            </div>
          ) : error ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <p style={{ color: "#F43F5E", fontSize: 14, fontWeight: 600 }}>{error}</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
              {/* Shortest Variables Section */}
              <div>
                <h3 style={{ margin: "0 0 4px 0", fontSize: 16, fontWeight: 700, color: "#0C172C" }}>
                  Shortest Variables
                </h3>
                {/* <p style={{ fontSize: 11, color: "#798498", margin: "0 0 12px 0" }}>
                  Verify spacing and alignment with the shortest variable values
                </p> */}
                <div style={{ display: "flex", gap: 16 }}>
                  {shortestImages.map((url, idx) =>
                    renderImageCard(url, `Shortest preview ${idx + 1}`, `short-${idx}`)
                  )}
                  {shortestImages.length === 0 && (
                    <div
                      style={{
                        flex: 1,
                        border: "1px solid #E2E2FC",
                        borderRadius: 8,
                        padding: 40,
                        background: "#FAFAFE",
                        textAlign: "center",
                      }}
                    >
                      <span style={{ color: "#798498", fontSize: 12 }}>No preview available</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Longest Variables Section */}
              <div>
                <h3 style={{ margin: "0 0 4px 0", fontSize: 16, fontWeight: 700, color: "#0C172C" }}>
                  Longest Variables
                </h3>
                {/* <p style={{ fontSize: 11, color: "#798498", margin: "0 0 12px 0" }}>
                  Check for overflow, wrapping, and truncation with the longest variable values
                </p> */}
                <div style={{ display: "flex", gap: 16 }}>
                  {longestImages.map((url, idx) => renderImageCard(url, `Longest preview ${idx + 1}`, `long-${idx}`))}
                  {longestImages.length === 0 && (
                    <div
                      style={{
                        flex: 1,
                        border: "1px solid #E2E2FC",
                        borderRadius: 8,
                        padding: 40,
                        background: "#FAFAFE",
                        textAlign: "center",
                      }}
                    >
                      <span style={{ color: "#798498", fontSize: 12 }}>No preview available</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{ padding: "12px 24px", borderTop: "1px solid #E2E2FC", display: "flex", justifyContent: "flex-end" }}
        >
          <button
            onClick={onClose}
            style={{
              padding: "8px 20px",
              fontSize: 12,
              fontWeight: 600,
              color: "#FFF",
              background: "#F43F5E",
              border: "none",
              borderRadius: 8,
              cursor: "pointer",
              fontFamily: '"Inter", sans-serif',
            }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Lightbox overlay for expanded image */}
      {expandedImage && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setExpandedImage(null)}
        >
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.75)" }} />
          <div
            style={{
              position: "relative",
              maxWidth: "90vw",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 12,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <div
              style={{
                alignSelf: "flex-end",
                cursor: "pointer",
                background: "rgba(255,255,255,0.9)",
                borderRadius: "50%",
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
              }}
              onClick={() => setExpandedImage(null)}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                <path
                  d="M5 5L15 15M15 5L5 15"
                  stroke="#0C172C"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {/* Label */}
            <div
              style={{
                color: "#FFF",
                fontSize: 14,
                fontWeight: 600,
                textShadow: "0 1px 4px rgba(0,0,0,0.5)",
              }}
            >
              {expandedImage.label}
            </div>

            {/* Image */}
            <img
              src={expandedImage.url}
              alt={expandedImage.label}
              style={{
                maxWidth: "85vw",
                maxHeight: "80vh",
                borderRadius: 8,
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
                objectFit: "contain",
                background: "#FFF",
              }}
            />

            {/* Hint */}
            <div style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>Click outside to close</div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

/**
 * Computes 2nd-best preview values from sample data (per-column, not per-row).
 * Picks the 2nd shortest/longest value per column. Fallback for small files
 * where streaming isn't used.
 */
function computeSecondPreviewValues(
  data: Record<string, string>[],
  mode: "shortest" | "longest"
): Record<string, string> {
  if (data.length <= 1) return computePreviewValues(data, mode);

  const columns = Object.keys(data[0]);
  const result: Record<string, string> = {};

  columns.forEach((col) => {
    const values = data.map((row) => row[col] || "");
    const sorted = [...values].sort((a, b) => (mode === "shortest" ? a.length - b.length : b.length - a.length));
    result[col] = sorted.length > 1 ? sorted[1] : sorted[0];
  });

  return result;
}

export default PreviewVariablesModal;
